import { Page } from '@/shared/pagination/export';
import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Form, FormControl, FormSelect, InputGroup } from 'react-bootstrap';
import dayjs from 'dayjs';
import axios from 'axios';
import { useTimer } from '@/shared/hooks/export';
import { authService } from '@/core/auth/export';
import { useSubscription, MessageServiceConstants } from '@/core/messaging/export';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import styles from './chat-view.module.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import { getChatEmbed } from '@/core/chat/services/chat-embed-registry';

interface User {
  _id: string;
  name: string;
  userId: string;
}

interface Conversation {
  _id: string;
  name: string;
  users: User[];
}

interface Message {
  _id?: string;
  message: string;
  user: User;
  createdAt: string;
}

const chatService = {
  getConversations: async (): Promise<Page<Conversation>> => {
    const r = await axios.get('/api/chat/conversations');

    return r.data;
  },
  getMessages: async (conversationId: string): Promise<Page<Message>> => {
    const r = await axios.get(`/api/chat/messages/${conversationId}`);

    return r.data;
  },
  postMessage: async (conversationId: string, message: Message): Promise<Page<Message>> => {
    const r = await axios.post(
      `/api/chat/messages/${conversationId}`,

      message,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );

    return r.data;
  },
};

const LinkRenderer = (props: any) => {
  const [href, text] = props.href.split('%7C');

  return (
    <a href={href} target="_blank" rel="noreferrer">
      {text || props.children}
    </a>
  );
};

const TableRenderer = (props: any) => (
  <div className={styles.tableScroll}>
    <table>{props.children}</table>
  </div>
);

const MAX_INPUT_HEIGHT = 128;

const EMBED_TOKEN = /\$widget:([\w-]+):([\w-]+)/;

const EmbedRenderer = memo((props: { kind: string; id: string }) => {
  const { kind, id } = props;
  const Embed = getChatEmbed(kind);
  if (!Embed) return null;

  return (
    <div key={`${kind}-${id}`} className={'bg-primary m-1 p-3 rounded-3 '}>
      <Embed id={id} />{' '}
    </div>
  );
});

const TextRenderer = (props: any) => {
  if (typeof props.children === 'string') {
    return (
      <>
        {props.children.split(/\s+/).map((chunk: string, index: number) => {
          const [, kind, id] = EMBED_TOKEN.exec(chunk) ?? [];
          if (kind && id && getChatEmbed(kind)) return <EmbedRenderer kind={kind} id={id} />;

          return <>{chunk + ' '}</>;
        })}
      </>
    );
  }
  console.log(props);
  return <>{props.children}</>;
};

const audio = new Audio('/sounds/veep.mp3');

const TextRendererMemo = React.memo(TextRenderer);

// Shared by both bubble variants so incoming and outgoing markdown can never
// drift apart.
const markdownComponents = {
  a: LinkRenderer,
  p: TextRendererMemo,
  table: TableRenderer,
};

export function ChatMainView({ onNewMessage }: { onNewMessage?: (message: Message) => void }) {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [conversation, setConversation] = useState<Conversation>();
  const [messages, setMessages] = useState<Message[]>([]);
  const [message, setMessage] = useState<string>('');
  const [userId, setUserId] = useState<string>();
  const messageContainerRef = useRef<HTMLDivElement>(null);
  const messageInputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const textarea = messageInputRef.current;

    if (!textarea) return;

    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, MAX_INPUT_HEIGHT)}px`;
  }, [message]);

  useEffect(() => {
    setUserId(authService.getSubjectData()?.id || undefined);
  }, []);

  useEffect(() => {
    chatService.getConversations().then(value => setConversations(value.content.reverse()));
  }, []);

  useEffect(() => {
    if (conversations.length === 0) return;

    setConversation(conversations[0]);
  }, [conversations]);

  useEffect(() => {
    if (!conversation) return;

    chatService.getMessages(conversation._id).then(value => setMessages(value.content.reverse()));
  }, [conversation]);

  useEffect(() => {
    if (!messageContainerRef.current) return;

    messageContainerRef.current.scrollTop = messageContainerRef.current.scrollHeight;
  }, [messages]);

  useTimer(500_000, () => {
    if (!conversation) return;

    chatService.getMessages(conversation._id).then(value => {
      setMessages(value.content.reverse());
    });
  });

  const isMe = (message: Message) => {
    return message.user.userId?.endsWith(userId || '');
  };

  useSubscription(MessageServiceConstants.CHAT_TOPIC, (message: Message) => {
    if (!conversation) return;
    chatService.getMessages(conversation._id).then(value => setMessages(value.content.reverse()));

    if (!isMe(message)) {
      try {
        if (onNewMessage) onNewMessage(message);
      } catch (e) {
        console.error('Error handling message: ', e);
      }

      try {
        audio.volume = 0.7;
        audio.play().then(value => {
          console.log('played');
          audio.currentTime = 0;
        });

        if ('Notification' in window) {
          Notification.requestPermission().then(permission => {
            if (permission === 'granted') {
              new Notification(message.user.name, {
                body: message.message,
                icon: '/logo32.png',
              });
            }
          });
        }
      } catch (e) {
        console.error('Error playing audio: ', e);
      }
    }
  });

  const postMessage = (message: string) => {
    if (!conversation) return;

    if (!message) return;

    chatService
      .postMessage(conversation._id, {
        message,
        user: {
          name: 'John Doe',
          _id: '123',
          userId: '123',
        },
        createdAt: new Date().toISOString(),
      })
      .then(value => {
        chatService
          .getMessages(conversation._id)
          .then(value => setMessages(value.content.reverse()));
      });

    setMessage('');
  };

  const onInputKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== 'Enter' || event.shiftKey) return;

    event.preventDefault();
    postMessage(message);
  };

  const savedMessages = useMemo(() => messages, [messages]);

  const messageView = (
    <div key={'message-view'}>
      {savedMessages.map(message => (
        <div key={message._id}>
          {!isMe(message) && (
            <div className={'small fw-bold mt-2 text-muted'}>
              {message.user.name} -{' '}
              <span className={'fw-light'}>
                {dayjs(message.createdAt).format('h:mm:ss A')}
              </span>{' '}
            </div>
          )}

          {isMe(message) ? (
            <div className={'w-100 mb-2 d-flex'}>
              <div className={'flex-grow-1 '} style={{ minWidth: '100px' }}></div>
              <div
                className={
                  'bg-body-tertiary text-white rounded-3 ms-auto flex-shrink-1 ' + styles.message
                }
              >
                <Markdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                  {message.message}
                </Markdown>
              </div>
            </div>
          ) : (
            <div className={'mb-2 mt-2 d-flex'}>
              <div className={'bg-primary-subtle rounded-3 flex-shrink-1 ' + styles.message}>
                <Markdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                  {message.message}
                </Markdown>
              </div>
              <div className={'flex-grow-1 w-25'}></div>
            </div>
          )}
        </div>
      ))}
    </div>
  );

  return (
    <div className={'container-element h-100 d-flex flex-column'}>
      <div>
        <FormSelect
          size={'sm'}
          onChange={e => setConversation(conversations.find(c => c._id === e.target.value))}
        >
          {conversations.map(conversation => (
            <option key={conversation._id} value={conversation._id}>
              {conversation.name}
            </option>
          ))}
        </FormSelect>
      </div>

      <div
        className={'mt-2 flex-fill overflow-auto container-responsive-text'}
        ref={messageContainerRef}
      >
        {conversation && messageView}
      </div>

      <Form
        onSubmit={e => {
          e.stopPropagation();
          e.preventDefault();
          postMessage(message);
        }}
      >
        <InputGroup size={'sm'} className={'mb-2 mt-2 align-items-end'}>
          <FormControl
            as="textarea"
            ref={messageInputRef}
            rows={1}
            className={styles.input}
            placeholder="Type your message here"
            value={message}
            onChange={e => setMessage(e.target.value)}
            onKeyDown={onInputKeyDown}
          />
          <Button type={'submit'} disabled={!message} variant={'outline-success'} size={'sm'}>
            <FontAwesomeIcon icon={faPaperPlane} />
          </Button>
        </InputGroup>
      </Form>
    </div>
  );
}
